import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { Receipt, Plus, Trash, WarningCircle, CheckCircle, CaretRight } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

const empty = {
  period_start: format(new Date(), "yyyy-MM-dd"),
  period_end: format(new Date(), "yyyy-MM-dd"),
  gross_pay: "",
  net_pay: "",
  paid_duty_hours: "",
  overtime_hours: "",
};

export default function Paycheques() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const currency = user?.currency || "CAD";

  const { data: cheques = [] } = useQuery({
    queryKey: ["paycheques"],
    queryFn: async () => (await api.get("/paycheques")).data,
  });

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/paycheques", payload)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["paycheques"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setForm(empty);
      setOpen(false);
      if (data.status === "Underpaid")
        toast.error(`Possible underpayment: ${money(data.shortfall, currency)} short.`);
      else toast.success("Paycheque saved — looks correct.");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/paycheques/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["paycheques"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Paycheque removed.");
    },
  });

  const submit = (e) => {
    e.preventDefault();
    createMut.mutate({
      period_start: form.period_start,
      period_end: form.period_end,
      gross_pay: parseFloat(form.gross_pay) || 0,
      net_pay: parseFloat(form.net_pay) || 0,
      paid_duty_hours: parseFloat(form.paid_duty_hours) || 0,
      overtime_hours: parseFloat(form.overtime_hours) || 0,
    });
  };

  const num = (v, set) => (e) => set({ ...form, [v]: e.target.value });

  return (
    <div className="animate-fade-up" data-testid="paycheques-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="overline text-primary">Module</p>
          <h1 className="text-3xl font-black tracking-tight mt-1">Paycheques</h1>
          <p className="text-muted-foreground text-sm mt-1">Reconcile pay received against expected earnings.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-paycheque-button" className="rounded-md font-semibold">
              <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Add Paycheque
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border" data-testid="paycheque-dialog">
            <DialogHeader><DialogTitle>New Paycheque</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Period Start</Label>
                  <div className="mt-1.5"><DatePicker testId="paycheque-start" value={form.period_start}
                    onChange={(d) => setForm({ ...form, period_start: d })} /></div>
                </div>
                <div>
                  <Label>Period End</Label>
                  <div className="mt-1.5"><DatePicker testId="paycheque-end" value={form.period_end}
                    onChange={(d) => setForm({ ...form, period_end: d })} /></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Gross Pay ({currency})</Label>
                  <Input type="number" step="0.01" min="0" required data-testid="paycheque-gross"
                    className="mt-1.5 bg-secondary/50" value={form.gross_pay} onChange={num("gross_pay", setForm)} placeholder="2280.00" />
                </div>
                <div>
                  <Label>Net Pay ({currency})</Label>
                  <Input type="number" step="0.01" min="0" required data-testid="paycheque-net"
                    className="mt-1.5 bg-secondary/50" value={form.net_pay} onChange={num("net_pay", setForm)} placeholder="1650.00" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Paid Duty Hrs on Cheque</Label>
                  <Input type="number" step="0.25" min="0" required data-testid="paycheque-pd-hours"
                    className="mt-1.5 bg-secondary/50" value={form.paid_duty_hours} onChange={num("paid_duty_hours", setForm)} placeholder="5" />
                </div>
                <div>
                  <Label>Overtime Hrs on Cheque</Label>
                  <Input type="number" step="0.25" min="0" required data-testid="paycheque-ot-hours"
                    className="mt-1.5 bg-secondary/50" value={form.overtime_hours} onChange={num("overtime_hours", setForm)} placeholder="4" />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={createMut.isPending} data-testid="paycheque-submit" className="rounded-md font-semibold">
                  {createMut.isPending ? "Checking…" : "Save & Verify"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {cheques.length === 0 ? (
        <Card className="p-12 bg-card border-border text-center" data-testid="paycheques-empty">
          <Receipt className="h-10 w-10 text-muted-foreground mx-auto mb-3" weight="bold" />
          <p className="text-muted-foreground">No paycheques recorded yet. Add one to verify your pay.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {cheques.map((c) => {
            const under = c.status === "Underpaid";
            return (
              <Card key={c.id} data-testid={`paycheque-row-${c.id}`}
                className={`p-5 border transition-transform duration-200 hover:-translate-y-0.5 ${
                  under ? "bg-destructive/10 border-destructive/40" : "bg-card border-border"
                }`}>
                <div className="flex items-center justify-between gap-4">
                  <Link to={`/paycheques/${c.id}`} className="flex items-center gap-4 min-w-0 flex-1" data-testid={`paycheque-link-${c.id}`}>
                    <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${
                      under ? "bg-destructive/20 text-destructive" : "bg-emerald-500/20 text-emerald-400"}`}>
                      {under ? <WarningCircle className="h-6 w-6" weight="fill" /> : <CheckCircle className="h-6 w-6" weight="fill" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">
                        {format(new Date(c.period_start + "T00:00:00"), "MMM d")} – {format(new Date(c.period_end + "T00:00:00"), "MMM d, yyyy")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Gross {money(c.gross_pay, currency)} · Expected {money(c.expected_gross, currency)}
                        {under && <span className="text-destructive font-semibold"> · Short {money(c.shortfall, currency)}</span>}
                      </p>
                    </div>
                  </Link>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      under ? "bg-destructive/20 text-destructive" : "bg-emerald-500/20 text-emerald-400"}`}
                      data-testid={`paycheque-status-${c.id}`}>{c.status}</span>
                    <button data-testid={`delete-paycheque-${c.id}`} onClick={() => deleteMut.mutate(c.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                      <Trash className="h-4 w-4" weight="bold" />
                    </button>
                    <Link to={`/paycheques/${c.id}`} className="text-muted-foreground"><CaretRight className="h-4 w-4" weight="bold" /></Link>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
